/**
 * MobileLeavesView
 *
 * Mobile card-based replacement for the PrimeReact DataTable in LeavesView.
 * Displays each leave request as a touch-friendly card with status, dates,
 * duration, and contextual action buttons (validate / reject / delete).
 */
import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  Pagination,
  PaginationItem,
  Divider,
  Fade,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import EventNoteRoundedIcon from "@mui/icons-material/EventNoteRounded";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import Langs, { Localize } from "../../../app/lang/langs";
import {
  checkPermission,
  isOwner,
  getTagClass,
  getDayPeriodFromDate,
} from "../../../app/utils/helpers";
import { config } from "../../../app/utils/config";
import moment from "moment";

const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";

const severityToColor = {
  success: "success",
  info: "info",
  warning: "warning",
  danger: "error",
  null: "default",
  undefined: "default",
};

const PAGE_SIZE = 20;

/**
 * @param {Object} props
 * @param {Array}   props.leaves        - leaves2 from redux (raw list with timestamps)
 * @param {string}  props.userId        - current user id
 * @param {number}  props.userRoleId    - current user role
 * @param {Object}  props.myTeam        - user's team
 * @param {Function} props.onValidate   - (id) => void
 * @param {Function} props.onReject     - (id) => void
 * @param {Function} props.onDelete     - (rowData) => void
 * @param {Function} props.onRequestDelete - (rowData) => void
 * @param {Function} [props.onOpenPlanning] - (rowData) => void, opens the planning on the leave
 * @param {React.ReactNode} [props.header] - shown above the search bar (pending requests panel)
 */
export default function MobileLeavesView({
  leaves,
  userId,
  userRoleId,
  myTeam,
  onValidate,
  onReject,
  onDelete,
  onRequestDelete,
  onOpenPlanning,
  header,
}) {
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);

  function isManager(teamId) {
    return checkPermission(
      config.scopes.leaves,
      config.permissionTypes.edit,
      teamId
    );
  }

  function canManage(rowData) {
    return (
      isOwner(userRoleId) ||
      isManager(rowData.id_team)
    )
  }

  function formatLeaveDate(timestamp) {
    if (!timestamp) return "";
    return moment(timestamp).format("DD/MM/YYYY");
  }

  function getStatusChipColor(status) {
    const severity = getTagClass(status);
    return severityToColor[severity] || "default";
  }

  const filteredData = useMemo(() => {
    const base = leaves || [];
    if (!searchText.trim()) return base;

    const terms = searchText.toLowerCase().split(" ").filter(Boolean);
    return base.filter((leave) => {
      const searchStr = [
        leave.nom,
        leave.prenom,
        leave.type,
        leave.status,
        leave.company,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return terms.every((term) => searchStr.includes(term));
    });
  }, [leaves, searchText]);

  React.useEffect(() => {
    setPage(1);
  }, [searchText]);

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const paginatedData = filteredData.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  return (
    <Box
      sx={{
        px: 2,
        pt: 1.5,
        // Clears the home indicator so the pagination row stays tappable.
        pb: "calc(20px + env(safe-area-inset-bottom))",
        // Fills whatever LeavesView's column leaves after the bar and the tabs. This was
        // `mt: "130px"` + `height: calc(100vh - 130px)`: two guesses at a fixed bar's height
        // that no longer have to be made, and that were wrong by the 41px the sub-tab strip
        // occupied — the blank band under the header, and 41px of pagination below the fold.
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {header}

      {/* Search bar */}
      <TextField
        fullWidth
        size="small"
        placeholder={Localize("Search") + "..."}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ opacity: 0.5 }} />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 1,
          "& input": { fontSize: "16px" },
          "& .MuiOutlinedInput-root": {
            borderRadius: "14px",
            bgcolor: "rgba(255,255,255,0.75)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            border: "1px solid rgba(0,0,0,0.06)",
            transition: TRANSITION_SMOOTH,
            "& fieldset": { border: "none" },
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.9)",
            },
            "&.Mui-focused": {
              bgcolor: "rgba(255,255,255,0.95)",
              boxShadow: "0 0 0 3px rgba(255,144,21,0.15)",
              border: "1px solid rgba(255,144,21,0.3)",
            },
          },
        }}
      />

      {/* Results count */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          mb: 1,
          px: 0.5,
          fontWeight: 500,
          letterSpacing: "0.02em",
          opacity: 0.7,
        }}
      >
        {filteredData.length} {Localize("results")}
      </Typography>

      {/* Card list */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          scrollBehavior: "smooth",
          pb: 2,
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0,0,0,0.1)",
            borderRadius: 2,
          },
        }}
      >
        {paginatedData.map((leave, index) => (
          <Fade in key={leave.id} timeout={200 + index * 40}>
            <Card
              elevation={0}
              sx={{
                mb: 1.5,
                borderRadius: "16px",
                overflow: "hidden",
                bgcolor: "rgba(255,255,255,0.85)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow:
                  "0 2px 8px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.06)",
                transition: TRANSITION_SMOOTH,
                "&:active": {
                  transform: "scale(0.98)",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
                },
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                {/* Top row: name + status chip */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 0.75,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: 0,
                    }}
                  >
                    {/* Avatar circle */}
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        bgcolor: "warning.light",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: "0.85rem",
                        flexShrink: 0,
                      }}
                    >
                      {(leave.prenom?.[0] || "").toUpperCase()}
                      {(leave.nom?.[0] || "").toUpperCase()}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.9rem",
                          lineHeight: 1.3,
                          letterSpacing: "-0.01em",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {leave.prenom} {leave.nom}
                      </Typography>
                      {/* Company, and the team the collab is on today, as the grid shows it. */}
                      {(leave.company || leave.currentTeamName) && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.75rem", opacity: 0.75, display: "block" }}
                        >
                          {[leave.company, leave.currentTeamName].filter(Boolean).join(" · ")}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Status chip */}
                  {leave.status && (
                    <Chip
                      label={<Langs str={leave.status} />}
                      size="small"
                      color={getStatusChipColor(leave.status)}
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.7rem",
                        height: 24,
                        borderRadius: "8px",
                        transition: TRANSITION_SMOOTH,
                      }}
                    />
                  )}
                </Box>

                {/* Leave type chip */}
                {leave.type && (
                  <Chip
                    label={leave.type}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: "0.7rem",
                      height: 22,
                      mb: 0.75,
                      borderRadius: "8px",
                      transition: TRANSITION_SMOOTH,
                    }}
                  />
                )}

                {/* Date range row */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    mt: 0.5,
                  }}
                >
                  <EventNoteOutlinedIcon
                    sx={{ fontSize: 16, color: "text.secondary", opacity: 0.75 }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.78rem", opacity: 0.75 }}
                  >
                    {formatLeaveDate(leave.fromDate)}{" "}
                    {getDayPeriodFromDate(leave.fromDate)}
                    {"  \u2192  "}
                    {formatLeaveDate(leave.toDate)}{" "}
                    {getDayPeriodFromDate(leave.toDate)}
                  </Typography>
                </Box>

                {/* Duration row */}
                {leave.duration != null && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      mt: 0.25,
                    }}
                  >
                    <AccessTimeOutlinedIcon
                      sx={{ fontSize: 16, color: "text.secondary", opacity: 0.75 }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.78rem", opacity: 0.75 }}
                    >
                      {leave.duration}
                    </Typography>
                  </Box>
                )}
              </CardContent>

              {/* Action buttons row */}
              <Divider sx={{ opacity: 0.4, borderColor: "rgba(0,0,0,0.06)" }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  px: 1,
                  py: 0.25,
                }}
              >
                {onOpenPlanning && leave.fromDate && (
                  <Tooltip title={<Langs str="WORK_STOPPAGE_OPEN_PLANNING" />}>
                    <IconButton
                      size="small"
                      onClick={() => onOpenPlanning(leave)}
                      sx={{
                        minWidth: 44,
                        minHeight: 44,
                        borderRadius: "12px",
                        mr: "auto",
                        transition: TRANSITION_SMOOTH,
                        "&:hover": { backgroundColor: "rgba(255,144,21,0.08)", transform: "scale(1.08)" },
                        "&:active": { transform: "scale(0.95)" },
                      }}
                    >
                      <EventNoteRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {canManage(leave) ? (
                  <>
                    {["PENDING"].includes(leave.status) && (
                      <Tooltip title={<Langs str="VALIDATE" />}>
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => onValidate(leave.id)}
                          sx={{
                            minWidth: 44,
                            minHeight: 44,
                            borderRadius: "12px",
                            transition: TRANSITION_SMOOTH,
                            "&:hover": {
                              backgroundColor: "rgba(46,125,50,0.08)",
                              transform: "scale(1.08)",
                            },
                            "&:active": { transform: "scale(0.95)" },
                          }}
                        >
                          <CheckCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {["PENDING"].includes(leave.status) && (
                      <Tooltip title={<Langs str="REJECT" />}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onReject(leave.id)}
                          sx={{
                            minWidth: 44,
                            minHeight: 44,
                            borderRadius: "12px",
                            transition: TRANSITION_SMOOTH,
                            "&:hover": {
                              backgroundColor: "rgba(211,47,47,0.08)",
                              transform: "scale(1.08)",
                            },
                            "&:active": { transform: "scale(0.95)" },
                          }}
                        >
                          <CancelOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {!["DELETED"].includes(leave.status) && (
                      <Tooltip title={<Langs str="Delete" />}>
                        <IconButton
                          size="small"
                          onClick={() => onDelete(leave)}
                          sx={{
                            minWidth: 44,
                            minHeight: 44,
                            borderRadius: "12px",
                            transition: TRANSITION_SMOOTH,
                            "&:hover": {
                              backgroundColor: "rgba(255,144,21,0.08)",
                              transform: "scale(1.08)",
                            },
                            "&:active": { transform: "scale(0.95)" },
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                ) : (
                  <>
                    {String(leave.id_collab) === String(userId) &&
                      !["DELETED", "DELETE_REQUESTED"].includes(
                        leave.status
                      ) && (
                        <Tooltip title={<Langs str="REQUEST_DELETE" />}>
                          <IconButton
                            size="small"
                            onClick={() => onRequestDelete(leave)}
                            sx={{
                              minWidth: 44,
                              minHeight: 44,
                              borderRadius: "12px",
                              transition: TRANSITION_SMOOTH,
                              "&:hover": {
                                backgroundColor: "rgba(255,144,21,0.08)",
                                transform: "scale(1.08)",
                              },
                              "&:active": { transform: "scale(0.95)" },
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                  </>
                )}
              </Box>
            </Card>
          </Fade>
        ))}

        {paginatedData.length === 0 && (
          <Fade in timeout={400}>
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ opacity: 0.5 }}
              >
                <Langs str="No results" />
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => setPage(value)}
            size="small"
            color="warning"
            renderItem={(item) => (
              <PaginationItem
                {...item}
                sx={{
                  borderRadius: "10px",
                  transition: TRANSITION_SMOOTH,
                  "&:hover": {
                    transform: "scale(1.08)",
                  },
                  "&.Mui-selected": {
                    boxShadow: "0 2px 8px rgba(255,144,21,0.35)",
                  },
                }}
              />
            )}
          />
        </Box>
      )}
    </Box>
  );
}
